import { Text, Title } from '@vkontakte/vkui';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { platform } from 'reactApp/appHelpers/configHelpers';
import { emitAnalyticEvent } from 'reactApp/appHelpers/experimentAnalytic';
import { AnalyticEventNames } from 'reactApp/appHelpers/experimentAnalytic/eventNames';
import { BaseConfirmDialog } from 'reactApp/components/BaseConfirmDialog/BaseConfirmDialog';
import { EButtonType } from 'reactApp/components/BaseConfirmDialog/BaseConformationDialog.types';
import { EnvironmentSelectors } from 'reactApp/modules/environment/environment';
import { goToPromoQuota } from 'reactApp/sections/QuotaLanding/QuotaLanding.helpers';
import { AsidePromoModal } from 'reactApp/ui/AsidePromoModal/AsidePromoModal';
import { EDTIOR_RESTRICTION_SOURCES, getUtmQuery } from 'reactApp/ui/EditorRestriction/EditorRestriction.const';
import { ESplashScreenAction, sendSplashScreenAnalytics } from 'reactApp/ui/Mobile/SplashScreen/SplashScreen.analytics';
import { sendDwh } from 'reactApp/utils/ga';

import styles from './EditorRestrictionModal.css';
import { useHandlers } from './useHandlers';

export interface EditorRestrictionModalProps {
    onClose?(): void;
}

const sendDesktopAnalytics = (action: ESplashScreenAction) =>
    sendDwh({
        eventCategory: 'balloon',
        action,
        dwhData: { name: EDTIOR_RESTRICTION_SOURCES.mnz_editor_less_512gb, platform },
    });

const sendTouchAnalytics = (action: ESplashScreenAction) =>
    sendSplashScreenAnalytics({ action, name: EDTIOR_RESTRICTION_SOURCES.mnz_editor_less_512gb });

export const EditorRestrictionModal = (props: EditorRestrictionModalProps) => {
    const isPhone = useSelector(EnvironmentSelectors.isPhone);
    return isPhone ? <EditorRestrictionPhoneModal {...props} /> : <EditorRestrictionAsideModal {...props} />;
};

const EditorRestrictionAsideModal = ({ onClose }: EditorRestrictionModalProps) => {
    const { handleBuyClick, handleClose, tariffQuotaValue, userQuotaValue, gbText, buttonText } = useHandlers({
        onClose,
        paysource: EDTIOR_RESTRICTION_SOURCES.balloon_editor_less_512gb,
        sendAnalytics: sendDesktopAnalytics,
    });
    const title = <>Редактирование&nbsp;&mdash; в&nbsp;тарифах от&nbsp;{tariffQuotaValue}</>;

    useEffect(() => {
        emitAnalyticEvent(AnalyticEventNames.EDITOR_BALLOON_SHOW_512);
        sendDesktopAnalytics(ESplashScreenAction.show);
    }, []);

    const goToTariffs = useCallback(() => {
        emitAnalyticEvent(AnalyticEventNames.EDITOR_TOOLTIP_CLICK_TARIFF_512);
        sendDesktopAnalytics(ESplashScreenAction.click_tariff);
        goToPromoQuota({ currentPage: true, query: getUtmQuery(EDTIOR_RESTRICTION_SOURCES.balloon_editor_less_512gb) });
    }, []);

    return (
        <AsidePromoModal
            title={title}
            text={
                <>
                    У вас {userQuotaValue}. {gbText} или выберите{' '}
                    <a className={styles.link} onClick={goToTariffs}>
                        другой тариф
                    </a>
                </>
            }
            buttonText={buttonText}
            showAboveViewer={true}
            onClick={handleBuyClick}
            onClose={handleClose}
        />
    );
};

const EditorRestrictionPhoneModal = ({ onClose }: EditorRestrictionModalProps) => {
    const { handleBuyClick, handleClose, tariffQuotaValue, userQuotaValue, gbText, buttonText } = useHandlers({
        onClose,
        paysource: EDTIOR_RESTRICTION_SOURCES.touch_editor_less_512gb,
        sendAnalytics: sendTouchAnalytics,
    });

    const title = () => <Title level="2">Доступно с тарифами от {tariffQuotaValue}</Title>;

    const content = () => (
        <Text>
            У вас — {userQuotaValue}. {gbText}, чтобы редактировать документы или выберите другой тариф
        </Text>
    );
    const buttons = useMemo(() => [EButtonType.accept, EButtonType.action], []);

    useEffect(() => {
        emitAnalyticEvent(AnalyticEventNames.EDITOR_SPLASHSCREEN_ALERT_SHOW_512);
        sendTouchAnalytics(ESplashScreenAction.show);
    }, []);

    const goToTariffs = useCallback(() => {
        emitAnalyticEvent(AnalyticEventNames.EDITOR_SPLASHSCREEN_ALERT_CLICK_TARIFF_512);
        sendTouchAnalytics(ESplashScreenAction.click_tariff);
        goToPromoQuota({ currentPage: true, query: getUtmQuery(EDTIOR_RESTRICTION_SOURCES.touch_editor_less_512gb) });
    }, []);

    return (
        <BaseConfirmDialog
            onSuccess={handleBuyClick}
            onAction={goToTariffs}
            onClose={handleClose}
            buttons={buttons}
            successButtonText={buttonText}
            actionButtonText="Выбрать другой тариф"
            renderContent={content}
            renderHeader={title}
            buttonTheme="vk"
            size="tiny"
        />
    );
};

EditorRestrictionPhoneModal.displayName = 'EditorRestrictionPhoneModal';

EditorRestrictionAsideModal.displayName = 'EditorRestrictionAsideModal';
